import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getQuestions, getQuestion, createQuestion, updateQuestion, deleteQuestion,
  toggleQuestionActive, importQuestionsExcel, downloadQuestionTemplate,
  downloadBusinessQuestionTemplate,
  getDimensions, getQuestionMeta, bulkDeleteQuestions,
  getQuestionImportBatches, importBusinessQuestionsExcel,
  reviseQuestionImportBatch, retryQuestionImportBatch,
  confirmQuestionImportBatch, downloadQuestionImportBatch,
} from '../../api/admin';
import AdminLayout from '../../components/AdminLayout';
import Button from '../../components/Button';
import Alert from '../../components/Alert';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../context/AuthContext';

const TYPE_LABEL = { ability: '能力题' };
const OPTION_LETTERS = ['A', 'B', 'C', 'D'];

function deletionErrorMessage(error, fallback) {
  const data = error.response?.data;
  const message = Array.isArray(data) ? data : data?.detail;
  if (typeof message === 'string' && message.trim()) return message;
  if (Array.isArray(message)) {
    return message.filter(item => typeof item === 'string' && item.trim()).join('；') || fallback;
  }
  return fallback;
}

const emptyOptions = () => OPTION_LETTERS.map(letter => ({
  letter, text: '', subtitle: '', main_score: 0,
}));
const EMPTY_FORM = {
  question_type: 'ability',
  dimension: '',
  stem: '', scenario_text: '', scenario_tag: '',
  options: emptyOptions(),
  target_phase: '', behavior_indicator: '',
  is_active: true,
};

// 4 选项主分必须恰好是 {1,2,3,4} 的排列（与后端 / Excel 导入同口径）
function scoresAreValid(options) {
  const scores = options.map(o => Number(o.main_score)).filter(s => s >= 1 && s <= 4);
  if (scores.length !== 4) return false;
  return scores.slice().sort().join(',') === '1,2,3,4';
}

const BATCH_STATUS = {
  uploaded: { label: '待转换', color: '#64748b', bg: '#f1f5f9' },
  processing: { label: '转换中', color: '#1d4ed8', bg: '#eff6ff' },
  review_required: { label: '待修正', color: '#b45309', bg: '#fffbeb' },
  ready: { label: '待确认', color: '#047857', bg: '#ecfdf5' },
  imported: { label: '已入库', color: '#166534', bg: '#f0fdf4' },
  failed: { label: '转换失败', color: '#b91c1c', bg: '#fef2f2' },
  superseded: { label: '历史批次', color: '#64748b', bg: '#f8fafc' },
};

export default function AdminQuestionsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const [questions, setQuestions] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [scenarioTags, setScenarioTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const toast = useToast();
  const notifySuccess = (message) => toast(message, 'success');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterDim, setFilterDim] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [page, setPage] = useState(1);
  const [jumpPage, setJumpPage] = useState('');
  const [pageError, setPageError] = useState('');
  const listRequestId = useRef(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const legacyFileRef = useRef();
  const businessFileRef = useRef();
  const revisionFileRef = useRef();
  const [revisionBatchId, setRevisionBatchId] = useState(null);
  const [batches, setBatches] = useState([]);
  const [batchPage, setBatchPage] = useState(1);
  const [batchTotal, setBatchTotal] = useState(0);
  const batchRequestId = useRef(0);
  const batchTotalPages = Math.max(1, Math.ceil(batchTotal / 10));
  const [showBatchHistory, setShowBatchHistory] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchActionId, setBatchActionId] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState({});

  const [selected, setSelected] = useState(new Set());
  const selectableQuestions = questions;
  const allChecked = isSuperAdmin && selectableQuestions.length > 0
    && selectableQuestions.every(q => selected.has(q.id));
  const toggleAll = () => setSelected(
    allChecked ? new Set() : new Set(selectableQuestions.map(q => q.id)),
  );
  const toggleOne = (id) => setSelected(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  const tagName = (code) => {
    const hit = scenarioTags.find(t => t.code === code);
    return hit ? hit.name : (code || '—');
  };

  const load = async () => {
    const requestId = ++listRequestId.current;
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (appliedSearch) params.search = appliedSearch;
      if (filterDim) params.dimension = filterDim;
      if (filterStatus) params.is_active = filterStatus;
      if (filterSource) params.source = filterSource;
      const { data } = await getQuestions(params);
      if (requestId !== listRequestId.current) return;
      setQuestions(data.results ?? data);
      setTotal(data.count ?? (data.results ?? data).length);
    } catch (err) {
      if (requestId === listRequestId.current) {
        if (err.response?.status === 404 && page > 1) setPage(1);
        else setError('加载失败');
      }
    }
    finally { if (requestId === listRequestId.current) setLoading(false); }
  };

  useEffect(() => {
    setSelected(new Set());
    setJumpPage('');
    setPageError('');
    load();
  }, [page, filterDim, filterStatus, filterSource, appliedSearch]); // eslint-disable-line
  useEffect(() => {
    getDimensions().then(({ data }) => setDimensions(data.results ?? data)).catch(() => {});
    getQuestionMeta()
      .then(({ data }) => {
        setScenarioTags(data.scenario_tags || []);
      })
      .catch(() => {});
  }, []);

  const loadBatches = async () => {
    const requestId = ++batchRequestId.current;
    setBatchLoading(true);
    try {
      const { data } = await getQuestionImportBatches({ page: batchPage });
      if (requestId !== batchRequestId.current) return;
      setBatches(data?.results ?? data ?? []);
      setBatchTotal(data?.count ?? data?.length ?? 0);
    } catch (err) {
      if (requestId === batchRequestId.current) {
        if (err.response?.status === 404 && batchPage > 1) setBatchPage(1);
        else setError('题库转换批次加载失败');
      }
    } finally {
      if (requestId === batchRequestId.current) setBatchLoading(false);
    }
  };

  useEffect(() => { loadBatches(); }, [batchPage]); // eslint-disable-line
  useEffect(() => {
    if (!showBatchHistory || !batches.some(item => ['uploaded', 'processing'].includes(item.status))) return undefined;
    const timer = window.setInterval(loadBatches, 3000);
    return () => window.clearInterval(timer);
  }, [batches, showBatchHistory, batchPage]); // eslint-disable-line

  const openCreate = () => { setEditItem(null); setForm(EMPTY_FORM); setError(''); setShowModal(true); };

  const openEdit = async (q) => {
    setError('');
    try {
      const { data } = await getQuestion(q.id);
      const opts = Array.isArray(data.options) && data.options.length === 4
        ? data.options.map((o, i) => ({
            letter: o.letter || OPTION_LETTERS[i],
            text: o.text || '',
            subtitle: o.subtitle || '',
            explanation: o.explanation || '',
            main_score: o.main_score || 0,
          }))
        : emptyOptions();
      setEditItem(data);
      setForm({
        question_type: data.question_type,
        dimension: data.dimension || '',
        stem: data.stem || '',
        scenario_text: data.scenario_text || '',
        scenario_tag: data.scenario_tag || '',
        options: opts,
        target_phase: data.target_phase || '',
        behavior_indicator: data.behavior_indicator || '',
        is_active: data.is_active ?? true,
      });
      setShowModal(true);
    } catch {
      setError('加载题目详情失败，请重试');
    }
  };

  const setOption = (idx, field, value) => {
    setForm(f => {
      const options = f.options.slice();
      options[idx] = { ...options[idx], [field]: value };
      return { ...f, options };
    });
  };

  const validateForm = () => {
    if (!form.stem.trim()) return '题干不能为空';
    if (!form.scenario_tag) return '请选择场景标签';
    if (!form.dimension) return '能力题必须选择所属能力';
    if (form.options.some(o => !o.text.trim())) return '4 个选项文案都必须填写';
    if (!scoresAreValid(form.options)) return '4 个选项主分必须是 1/2/3/4 各一个';
    return '';
  };

  const handleSave = async () => {
    const msg = validateForm();
    if (msg) { setError(msg); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        question_type: form.question_type,
        stem: form.stem.trim(),
        scenario_text: form.scenario_text.trim(),
        scenario_tag: form.scenario_tag,
        dimension: form.dimension || null,
        options: form.options.map(o => ({
          letter: o.letter,
          text: o.text.trim(),
          subtitle: o.subtitle.trim(),
          explanation: (o.explanation || '').trim(),
          main_score: Number(o.main_score),
        })),
        target_phase: form.target_phase || '',
        behavior_indicator: form.behavior_indicator.trim(),
        is_active: form.is_active,
      };
      if (editItem) await updateQuestion(editItem.id, payload);
      else await createQuestion(payload);
      notifySuccess(editItem ? '修改成功' : '新增成功');
      setShowModal(false);
      load();
    } catch (e) {
      const data = e.response?.data;
      setError(data?.detail || (data ? JSON.stringify(data) : '保存失败'));
    } finally { setSaving(false); }
  };

  const handleDelete = async (q) => {
    if (!window.confirm(`确认删除题目：${q.stem.slice(0, 30)}？`)) return;
    setError('');
    try { await deleteQuestion(q.id); notifySuccess('已删除'); load(); }
    catch (err) { setError(deletionErrorMessage(err, '删除失败')); }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`确认删除选中的 ${selected.size} 道题目？`)) return;
    setError('');
    try {
      const { data } = await bulkDeleteQuestions([...selected]);
      notifySuccess(`已删除 ${data.deleted} 道题目`);
      setSelected(new Set());
      load();
    } catch (err) { setError(deletionErrorMessage(err, '批量删除失败')); }
  };

  const handleToggle = async (q) => {
    try { await toggleQuestionActive(q.id); load(); }
    catch { setError('操作失败'); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const { data } = await downloadQuestionTemplate();
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AIQ题库导入模版.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('模版下载失败，请重试');
    }
  };

  const handleLegacyImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const { data } = await importQuestionsExcel(file);
      if (data.errors && data.errors.length > 0) {
        setError(`${data.message}；错误详情：${data.errors.slice(0, 3).join('；')}${data.errors.length > 3 ? `…等 ${data.errors.length} 条` : ''}`);
      } else {
        notifySuccess(data.message || '旧版题库导入完成');
      }
      if (data.created > 0) load();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || '旧版题库导入失败');
    } finally { e.target.value = ''; }
  };

  const handleDownloadBusinessTemplate = async () => {
    try {
      const { data } = await downloadBusinessQuestionTemplate();
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AI领导力测评第一阶段_业务题库填写版.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('业务题库模板下载失败，请重试');
    }
  };

  const handleBusinessImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');

    try {
      const { data } = await importBusinessQuestionsExcel(file);
      setShowBatchHistory(true);
      if (data.status === 'failed') {
        setError(`批次 #${data.id} 转换失败，请查看错误详情`);
      } else if (data.reprocessed) {
        const result = data.status === 'ready'
          ? '重新校验通过，等待确认入库'
          : data.status === 'review_required' ? '重新校验后仍有问题待修正' : '正在重新校验';
        notifySuccess(`批次 #${data.id} ${result}`);
      } else if (data.idempotent_reuse) {
        notifySuccess(`文件已存在，已定位到批次 #${data.id}`);
      } else if (data.status === 'review_required') {
        notifySuccess(`已创建批次 #${data.id}，校验后仍有问题待修正`);
      } else if (data.status === 'ready') {
        notifySuccess(`已创建批次 #${data.id}，校验通过，等待确认入库`);
      } else {
        notifySuccess(`已创建批次 #${data.id}，正在后台转换`);
      }
      if (batchPage === 1) await loadBatches();
      else setBatchPage(1);
    } catch (err) {
      setError(err.response?.data?.detail || '业务题库上传失败');
    } finally { e.target.value = ''; }
  };

  const handleDownloadBatch = async (batch) => {
    setBatchActionId(batch.id);
    try {
      const { data } = await downloadQuestionImportBatch(batch.id);
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `题库校验修订表_批次${batch.id}_修订${batch.revision}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || '校验修订表下载失败');
    } finally { setBatchActionId(null); }
  };

  const startRevision = (batchId) => {
    setRevisionBatchId(batchId);
    revisionFileRef.current?.click();
  };

  const handleRevisionUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !revisionBatchId) return;
    setBatchActionId(revisionBatchId);
    setError('');

    try {
      const { data } = await reviseQuestionImportBatch(revisionBatchId, file);
      notifySuccess(data.status === 'review_required'
        ? `批次 #${revisionBatchId} 修订表已重新校验，仍有问题待修正`
        : `批次 #${revisionBatchId} 修订表已重新校验`);
      await loadBatches();
    } catch (err) {
      setError(err.response?.data?.detail || '修订表上传失败');
    } finally {
      setBatchActionId(null);
      setRevisionBatchId(null);
      e.target.value = '';
    }
  };

  const handleRetryBatch = async (batchId) => {
    setBatchActionId(batchId);
    setError('');

    try {
      const { data } = await retryQuestionImportBatch(batchId);
      if (data.status === 'review_required') {
        notifySuccess(`批次 #${batchId} 已重新转换，仍有问题待修正`);
      } else if (data.status === 'ready') {
        notifySuccess(`批次 #${batchId} 已重新转换并通过校验`);
      } else if (data.status === 'failed') {
        setError(`批次 #${batchId} 重新转换失败，请查看错误详情`);
      } else {
        notifySuccess(`批次 #${batchId} 正在重新转换`);
      }
      await loadBatches();
    } catch (err) {
      setError(err.response?.data?.detail || '批次重试失败');
    } finally { setBatchActionId(null); }
  };

  const handleConfirmBatch = async (batch) => {
    if (!window.confirm(`确认把批次 #${batch.id} 的 ${batch.valid_rows} 道题整批入库？将追加到题库并启用，保留已有题目；入库后仍可编辑、启停和删除。`)) return;
    setBatchActionId(batch.id);
    setError('');

    try {
      const { data } = await confirmQuestionImportBatch(batch.id);
      notifySuccess(data.message || '批次已确认入库');
      await Promise.all([loadBatches(), load()]);
    } catch (err) {
      setError(err.response?.data?.detail || '批次确认失败');
    } finally { setBatchActionId(null); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const handleJumpPage = (event) => {
    event.preventDefault();
    const target = Number(jumpPage);
    if (!/^\d+$/.test(jumpPage.trim()) || !Number.isSafeInteger(target) || target < 1 || target > totalPages) {
      setPageError(`请输入 1–${Math.max(1, totalPages)} 的整数页码`);
      return;
    }
    setPageError('');
    setPage(target);
  };
  const formScoresValid = scoresAreValid(form.options);

  return (
    <AdminLayout title="题库管理">
      {error && !showBatchHistory && <Alert type="error" style={{ marginBottom: 12 }}>{error}</Alert>}

      <section style={{
        marginBottom: 18, padding: 16, border: '1px solid #dbeafe',
        borderRadius: 12, background: '#f8fbff',
      }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>业务题库导入</div>
            <div style={{ marginTop: 4, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              支持业务填写版和下载的校验修订表；上传后仅做本地解析校验，不调用 AI。
              顶部上传用于建立新草稿，修改已有批次请在导入记录中选择“上传修订表”。
              下载校验修订表，按标红单元格和批注修改题目，再在原批次上传修订表；校验通过后确认入库，少量测试题也可导入。
              入库后追加启用，优先用于新测评；不足时由原有题库补位，新旧题均可场景化。批次仅用于追溯，题目仍可单独管理。
            </div>
          </div>
          <Button size="sm" variant="secondary" onClick={() => { setShowBatchHistory(true); loadBatches(); }}>导入记录（{batchTotal}）</Button>
          <Button size="sm" variant="secondary" onClick={handleDownloadBusinessTemplate}>下载业务模板</Button>
          {isSuperAdmin && (
            <Button size="sm" onClick={() => businessFileRef.current?.click()}>上传题库文件</Button>
          )}
          <input ref={businessFileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleBusinessImport} />
          <input ref={revisionFileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleRevisionUpload} />
        </div>
      </section>

      {showBatchHistory && (
        <Modal title="导入记录" maxWidth={1200} bodyKey={batchPage} onClose={() => setShowBatchHistory(false)} footer={
          <nav aria-label="导入记录分页" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>共 {batchTotal} 条 · 每页 10 条</span>
            <button disabled={batchLoading || batchPage <= 1} onClick={() => setBatchPage(p => p - 1)} style={pageBtn(!batchLoading && batchPage > 1)}>上一页</button>
            <span style={{ fontSize: 13 }}>{batchPage} / {batchTotalPages}</span>
            <button disabled={batchLoading || batchPage >= batchTotalPages} onClick={() => setBatchPage(p => p + 1)} style={pageBtn(!batchLoading && batchPage < batchTotalPages)}>下一页</button>
          </nav>
        }>
          {error && <Alert type="error" style={{ marginBottom: 12 }}>{error}</Alert>}
        {batchLoading && batches.length === 0 ? (
          <div style={{ padding: 16, color: '#64748b', fontSize: 13 }}>正在加载批次…</div>
        ) : batches.length === 0 ? (
          <div style={{ padding: 16, color: '#64748b', fontSize: 13, background: '#fff', borderRadius: 8 }}>
            暂无转换批次。首次上传后可在这里查看进度和错误定位。
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {batches.map(batch => {
              const statusMeta = BATCH_STATUS[batch.status] || BATCH_STATUS.uploaded;
              const isProcessing = batch.status === 'processing';
              const hasDetails = Boolean(isProcessing || batch.processing_error || batch.errors?.length || batch.warnings?.length);
              const expanded = expandedBatches[batch.id] ?? isProcessing;
              const canRetry = Boolean(batch.can_retry);
              const isStale = isProcessing && canRetry;
              const busy = batchActionId === batch.id
                || batch.status === 'uploaded'
                || (isProcessing && !canRetry);
              const processingSummary = batch.processing_summary || {};
              const totalRows = Number(batch.total_rows) || 0;
              const processedRows = Number(batch.processed_rows) || 0;
              const progressPercent = totalRows > 0
                ? Math.min(100, Math.round((processedRows / totalRows) * 100))
                : 0;
              return (
                <div key={batch.id} style={{ padding: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 9 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <strong style={{ color: '#0f172a', fontSize: 13 }}>#{batch.id} {batch.original_filename}</strong>
                    <span style={{
                      padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                      color: statusMeta.color, background: statusMeta.bg,
                    }}>{statusMeta.label}</span>
                    <span style={{ fontSize: 12, color: '#64748b' }}>
                      修订 {batch.revision} · 进度 {batch.processed_rows}/{batch.total_rows || '—'}
                      {!isProcessing && (
                        <> · 有效 {batch.valid_rows} · 错误 {batch.error_count} · 警告 {batch.warning_count}</>
                      )}
                    </span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {hasDetails && (
                        <button
                          type="button"
                          aria-expanded={expanded}
                          aria-controls={`batch-details-${batch.id}`}
                          onClick={() => setExpandedBatches(previous => ({ ...previous, [batch.id]: !expanded }))}
                          style={btnStyle('#475569')}
                        >
                          {expanded ? '收起结果 ▴' : '展开结果 ▾'}
                        </button>
                      )}
                      {batch.has_generated_file && !['uploaded', 'processing'].includes(batch.status) && (
                        <button disabled={busy} onClick={() => handleDownloadBatch(batch)} style={btnStyle('#2563eb')}>
                          下载校验修订表
                        </button>
                      )}
                      {isSuperAdmin && ['review_required', 'ready', 'failed'].includes(batch.status) && (
                        <button disabled={busy} onClick={() => startRevision(batch.id)} style={btnStyle('#7c3aed')}>
                          上传修订表
                        </button>
                      )}
                      {isSuperAdmin && canRetry && (
                        <button disabled={busy} onClick={() => handleRetryBatch(batch.id)} style={btnStyle('#d97706')}>
                          {isStale ? '重新转换' : '重试转换'}
                        </button>
                      )}
                      {isSuperAdmin && batch.status === 'ready' && (
                        <button disabled={busy} onClick={() => handleConfirmBatch(batch)} style={btnStyle('#059669')}>
                          确认整批入库
                        </button>
                      )}

                    </div>
                  </div>
                  <div id={`batch-details-${batch.id}`} hidden={!expanded}>
                  {batch.processing_error && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c' }}>{batch.processing_error}</div>
                  )}
                  {isProcessing && (
                    <div style={{
                      marginTop: 10, padding: '10px 12px', borderRadius: 8,
                      border: '1px solid #bfdbfe', background: '#eff6ff',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: 12, color: '#1e40af' }}>
                          {isStale ? '转换已中断' : '题库转换处理中'}
                        </strong>
                        <span style={{ fontSize: 12, color: '#475569' }}>
                          本轮已完成 {processedRows}/{totalRows || '—'}（{progressPercent}%）
                          {processingSummary.successful_rows != null && (
                            <> · 成功 {processingSummary.successful_rows} · 失败 {processingSummary.failed_rows || 0}</>
                          )}
                        </span>
                      </div>
                      <div style={{
                        height: 6, marginTop: 8, overflow: 'hidden',
                        borderRadius: 999, background: '#dbeafe',
                      }}>
                        <div style={{
                          width: `${progressPercent}%`, height: '100%', borderRadius: 999,
                          background: '#2563eb', transition: 'width 0.3s ease',
                        }} />
                      </div>
                      {processingSummary.recent_results?.length > 0 ? (
                        <div style={{ marginTop: 9, display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <div style={{ fontSize: 11, color: '#64748b' }}>最近处理结果</div>
                          {processingSummary.recent_results.map((item, index) => {
                            const succeeded = item.status === 'success';
                            return (
                              <div key={`${item.row}-${item.question_no}-${index}`} style={{
                                display: 'flex', gap: 8, alignItems: 'flex-start',
                                fontSize: 12, lineHeight: 1.5, color: '#334155',
                              }}>
                                <span style={{
                                  flex: '0 0 auto', padding: '1px 6px', borderRadius: 999,
                                  color: succeeded ? '#047857' : '#b91c1c',
                                  background: succeeded ? '#d1fae5' : '#fee2e2',
                                }}>{succeeded ? '成功' : '失败'}</span>
                                <div>
                                  <div>
                                    {item.row ? `第 ${item.row} 行 · ` : ''}{item.question || `题目 ${item.question_no || '—'}`}
                                    {succeeded && item.scenario_tag ? ` · 场景：${tagName(item.scenario_tag)}` : ''}
                                  </div>
                                  {!succeeded && item.message && (
                                    <div style={{ color: '#b91c1c' }}>{item.message}</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
                          {isStale
                            ? '该批次长时间没有进度，请点击“重新转换”。'
                            : '正在解析题目并校验分值，页面会自动刷新本轮结果。'}
                        </div>
                      )}
                    </div>
                  )}
                  {!isProcessing && batch.errors?.length > 0 && (
                    <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 7, background: '#fff7ed' }}>
                      {batch.errors.slice(0, 5).map((item, index) => (
                        <div key={`${item.code}-${item.row}-${index}`} style={{ fontSize: 12, color: '#9a3412', lineHeight: 1.6 }}>
                          {item.sheet}{item.row ? ` 第 ${item.row} 行` : ''}{item.question_no ? ` · 题 ${item.question_no}` : ''}
                          {item.field ? ` · ${item.field}` : ''}：{item.message}
                        </div>
                      ))}
                      {batch.errors.length > 5 && (
                        <div style={{ fontSize: 12, color: '#9a3412' }}>另有 {batch.errors.length - 5} 条，请下载校验修订表查看完整清单。</div>
                      )}
                    </div>
                  )}
                  {!isProcessing && batch.warnings?.length > 0 && (
                    <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 7, background: '#fffbeb' }}>
                      {batch.warnings.slice(0, 3).map((item, index) => (
                        <div key={`${item.code}-${item.row}-${index}`} style={{ fontSize: 12, color: '#92400e', lineHeight: 1.6 }}>
                          {item.sheet}{item.row ? ` 第 ${item.row} 行` : ''}{item.question_no ? ` · 题 ${item.question_no}` : ''}
                          {item.field ? ` · ${item.field}` : ''}：{item.message}
                        </div>
                      ))}
                      {batch.warnings.length > 3 && (
                        <div style={{ fontSize: 12, color: '#92400e' }}>另有 {batch.warnings.length - 3} 条，请下载校验修订表查看完整清单。</div>
                      )}
                    </div>
                  )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </Modal>
      )}

      {/* 工具栏 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              if (page === 1 && appliedSearch === search.trim()) load();
              else { setPage(1); setAppliedSearch(search.trim()); }
            }
          }}
          placeholder="搜索题干 / 场景标签… (回车)"
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, minWidth: 220 }}
        />
        <select aria-label="能力筛选" value={filterDim} onChange={e => { setFilterDim(e.target.value); setPage(1); }}
          style={selectStyle}>
          <option value="">全部能力</option>
          {dimensions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select aria-label="状态筛选" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">全部状态</option>
          <option value="true">启用</option>
          <option value="false">停用</option>
        </select>
        <select aria-label="来源筛选" value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">全部来源</option>
          <option value="ai">AI 生成</option>
          <option value="bank">题库</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {isSuperAdmin && selected.size > 0 && (
            <Button size="sm" variant="danger" onClick={handleBulkDelete}>
              删除所选 ({selected.size})
            </Button>
          )}
          {isSuperAdmin && (
            <>
              <Button size="sm" variant="secondary" onClick={handleDownloadTemplate}>下载旧版模板</Button>
              <Button size="sm" variant="secondary" onClick={() => legacyFileRef.current?.click()}>旧版 Excel 导入</Button>
              <input ref={legacyFileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleLegacyImport} />
              <Button size="sm" variant="secondary" onClick={() => navigate('/admin/recycle-bin')}>回收站</Button>
              <Button size="sm" onClick={openCreate}>+ 新增题目</Button>
            </>
          )}
        </div>
      </div>

      {/* 表格 */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0', width: 36 }}>
                <input type="checkbox" checked={allChecked} disabled={!isSuperAdmin} onChange={toggleAll} />
              </th>
              {['序号', '题干', '题型', '归属', '场景标签', '来源', '状态', '操作'].map(h => (
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>加载中...</td></tr>
            ) : questions.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>暂无数据</td></tr>
            ) : questions.map((q, index) => (
              <tr key={q.id} style={{ borderBottom: '1px solid #f1f5f9', background: selected.has(q.id) ? '#eff6ff' : undefined }}>
                <td style={{ padding: '10px 14px' }}>
                  <input
                    type="checkbox"
                    checked={selected.has(q.id)}
                    disabled={!isSuperAdmin}
                    onChange={() => toggleOne(q.id)}
                  />
                </td>
                <td style={{ padding: '10px 14px', color: '#94a3b8' }}>{(page - 1) * PAGE_SIZE + index + 1}</td>
                <td style={{ padding: '10px 14px', maxWidth: 340 }}>
                  <span title={q.import_batch ? `导入批次 #${q.import_batch}` : undefined} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {q.stem}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: '#eff6ff',
                    color: '#2563eb',
                  }}>
                    {TYPE_LABEL[q.question_type] || q.question_type}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', color: '#64748b' }}>
                  {q.dimension_name || '—'}
                </td>
                <td style={{ padding: '10px 14px', color: '#64748b' }}>{tagName(q.scenario_tag)}</td>
                <td style={{ padding: '10px 14px' }}>
                  {q.is_ai_generated
                    ? <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#ede9fe', color: '#7c3aed' }}>AI</span>
                    : <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#f1f5f9', color: '#64748b' }}>题库</span>
                  }
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: q.is_active ? '#f0fdf4' : '#fef2f2',
                    color: q.is_active ? '#16a34a' : '#dc2626',
                  }}>
                    {q.is_active ? '启用' : '停用'}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  {!isSuperAdmin ? (
                    <span style={{ fontSize: 11, color: '#64748b' }}>只读</span>
                  ) : <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => openEdit(q)} style={btnStyle('#2563eb')}>编辑</button>
                    <button onClick={() => handleToggle(q)} style={btnStyle(q.is_active ? '#d97706' : '#16a34a')}>
                      {q.is_active ? '停用' : '启用'}
                    </button>
                    <button onClick={() => handleDelete(q)} style={btnStyle('#dc2626')}>删除</button>
                  </div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 分页 */}
      <div style={{ marginTop: 16 }}>
        <form onSubmit={handleJumpPage} style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>共 {total} 条</span>
          <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={loading || page === 1} style={pageBtn(page > 1)}>上一页</button>
          <span style={{ padding: '6px 14px', fontSize: 13, color: '#64748b' }}>{page} / {Math.max(1, totalPages)}</span>
          <button type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={loading || page >= totalPages} style={pageBtn(page < totalPages)}>下一页</button>
          <label style={{ fontSize: 13, color: '#64748b' }}>跳至
            <input aria-label="跳转页码" value={jumpPage} onChange={e => { setJumpPage(e.target.value); setPageError(''); }} inputMode="numeric" style={{ ...inputStyle, width: 72, margin: '0 6px' }} />页
          </label>
          <button type="submit" disabled={loading || totalPages === 0} style={pageBtn(!loading && totalPages > 0)}>跳转</button>
        </form>
        {pageError && <div role="alert" style={{ textAlign: 'right', marginTop: 6, fontSize: 12, color: '#b91c1c' }}>{pageError}</div>}
      </div>

      {/* 新增/编辑弹窗 */}
      {showModal && (
        <Modal title={editItem ? '编辑题目' : '新增题目'} onClose={() => setShowModal(false)}>
          {editItem?.import_batch && (
            <p style={{ fontSize: 12, color: '#64748b' }}>导入批次 #{editItem.import_batch} · 修改不影响已开始测评的题目快照</p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="所属能力 *">
              <select value={form.dimension} onChange={e => setForm(f => ({ ...f, dimension: e.target.value }))} style={inputStyle}>
                <option value="">— 请选择 —</option>
                {dimensions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>

            <Field label="题干 *（主问句）">
              <textarea value={form.stem} onChange={e => setForm(f => ({ ...f, stem: e.target.value }))}
                rows={2} style={inputStyle} />
            </Field>

            <Field label="情境描述（可选；运营整句出题时合并进题干即可）">
              <textarea value={form.scenario_text} onChange={e => setForm(f => ({ ...f, scenario_text: e.target.value }))}
                rows={2} style={inputStyle} />
            </Field>

            <Field label="场景标签 *">
              <select value={form.scenario_tag} onChange={e => setForm(f => ({ ...f, scenario_tag: e.target.value }))} style={inputStyle}>
                <option value="">— 请选择 —</option>
                {scenarioTags.map(t => <option key={t.code} value={t.code}>{t.name}（{t.code}）</option>)}
              </select>
            </Field>

            <Field label="目标阶位（难度档，可空）">
              <select value={form.target_phase}
                onChange={e => setForm(f => ({ ...f, target_phase: e.target.value }))}
                style={inputStyle}>
                <option value="">— 未标注 —</option>
                <option value="L1">初用期（L1）</option>
                <option value="L2">惯用期（L2）</option>
                <option value="L3">扩散期（L3）</option>
                <option value="L4">重构期（L4）</option>
              </select>
            </Field>

            <Field label="行为指标编号（改写锚点，可空）">
              <input value={form.behavior_indicator}
                onChange={e => setForm(f => ({ ...f, behavior_indicator: e.target.value }))}
                placeholder="如 ai_symbiosis.L2，编号体系待运营" style={inputStyle} />
            </Field>

            <Field label="四选项（文案 + 副说明 + 主分；4 个主分须为 1/2/3/4 各一个）">
              {form.options.map((opt, i) => (
                <div key={opt.letter} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                  <span style={{ width: 18, fontWeight: 700, color: '#475569', paddingTop: 8 }}>{opt.letter}</span>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input value={opt.text} placeholder={`选项 ${opt.letter} 文案`}
                      onChange={e => setOption(i, 'text', e.target.value)} style={inputStyle} />
                    <input value={opt.subtitle} placeholder="副说明（灰色辅助说明，可留空）"
                      onChange={e => setOption(i, 'subtitle', e.target.value)}
                      style={{ ...inputStyle, fontSize: 12, color: '#64748b' }} />
                    <textarea value={opt.explanation || ''} placeholder={`选项 ${opt.letter} 解析`}
                      aria-label={`选项 ${opt.letter} 解析`}
                      onChange={e => setOption(i, 'explanation', e.target.value)}
                      style={{ ...inputStyle, fontSize: 12 }} />
                  </div>
                  <select value={opt.main_score} onChange={e => setOption(i, 'main_score', Number(e.target.value))}
                    style={{ ...inputStyle, width: 70 }}>
                    <option value={0}>主分</option>
                    {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              ))}
              <div style={{ fontSize: 12, color: formScoresValid ? '#16a34a' : '#dc2626', marginTop: 2 }}>
                {formScoresValid ? '✓ 主分组合合法' : '主分必须是 1 / 2 / 3 / 4 各一个，不能重复或缺漏'}
              </div>
            </Field>

            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={form.is_active}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              启用（参与抽题）
            </label>

            {error && <Alert type="error">{error}</Alert>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
              <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>取消</Button>
              <Button size="sm" onClick={handleSave} loading={saving}>保存</Button>
            </div>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}

const selectStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 };
const btnStyle = (color) => ({
  padding: '4px 10px', borderRadius: 6, border: `1px solid ${color}30`,
  background: `${color}0f`, color, fontSize: 12, cursor: 'pointer', fontWeight: 500,
});
const pageBtn = (active) => ({
  padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0',
  background: active ? '#fff' : '#f8fafc', color: active ? '#2563eb' : '#94a3b8',
  cursor: active ? 'pointer' : 'not-allowed', fontSize: 13,
});
const inputStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, width: '100%', boxSizing: 'border-box' };

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children, maxWidth = 640, footer, bodyKey }) {
  const dialogRef = useRef(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector('button')?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, []);
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
    if (event.key !== 'Tab') return;
    const controls = Array.from(dialogRef.current.querySelectorAll(
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]',
    )).filter(element => !element.closest('[hidden]'));
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={title} onKeyDown={handleKeyDown} style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth, maxHeight: '85vh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexShrink: 0 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{title}</h2>
          <button type="button" aria-label={`关闭${title}`} onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>
        <div key={bodyKey} style={{ overflowY: 'auto', minHeight: 0 }}>{children}</div>
        {footer && <div style={{ flexShrink: 0, paddingTop: 16, marginTop: 16, borderTop: '1px solid #e2e8f0' }}>{footer}</div>}
      </div>
    </div>
  );
}
